import { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Avatar } from "@progress/kendo-react-layout";
import { Input } from "@progress/kendo-react-inputs";
import { getAvatarGradientStyle, getInitials } from "../utils/avatar";
import { readFileAsDataUrl } from "../utils/formatters";

const toDraft = (user) => ({
  avatarDataUrl: user?.avatarDataUrl || "",
  email: user?.email || "",
  fullName: user?.fullName || ""
});

export default function Profile({ currentUser, isLoading, locale, onSave, t = {} }) {
  const inputRef = useRef(null);
  const [draft, setDraft] = useState(toDraft(currentUser));
  const userIdentity = draft.fullName || draft.email || "User";
  const userInitials = getInitials(userIdentity);
  const avatarStyle = getAvatarGradientStyle(userIdentity);

  useEffect(() => {
    setDraft(toDraft(currentUser));
  }, [currentUser]);

  const updateAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      return;
    }

    const avatarDataUrl = await readFileAsDataUrl(file);
    setDraft((current) => ({
      ...current,
      avatarDataUrl
    }));
    event.target.value = "";
  };

  return (
    <div className="page-stack">
      <section className="toolbar-panel">
        <div>
          <p className="eyebrow">{t.profile || "Profile"}</p>
          <h2>{t.accountIdentity || "Account identity"}</h2>
        </div>
        <Button
          disabled={isLoading}
          icon="save"
          onClick={() => onSave(draft)}
          themeColor="primary"
        >
          {t.saveProfile || "Save profile"}
        </Button>
      </section>

      <section className="profile-layout">
        <div className="panel profile-card">
          <input
            accept="image/*"
            className="hidden-input"
            onChange={updateAvatar}
            ref={inputRef}
            type="file"
          />
          <Avatar
            type={draft.avatarDataUrl ? "image" : "text"}
            shape="circle"
            className="profile-avatar gradient-avatar"
            style={avatarStyle}
          >
            {draft.avatarDataUrl ? <img alt="" src={draft.avatarDataUrl} /> : userInitials}
          </Avatar>
          <h3>{draft.fullName || t.yourProfile || "Your profile"}</h3>
          <p>{draft.email}</p>
          <Button fillMode="outline" icon="image" onClick={() => inputRef.current?.click()}>
            {t.uploadAvatar || "Upload avatar"}
          </Button>
        </div>

        <div className="panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{t.details || "Details"}</p>
              <h3>{t.personalInformation || "Personal information"}</h3>
            </div>
          </div>
          <div className="dialog-form">
            <div className="field full">
              <label>{t.name || "Name"}</label>
              <Input
                value={draft.fullName}
                onChange={(event) => setDraft({ ...draft, fullName: event.value })}
              />
            </div>
            <div className="field full">
              <label>{t.email || "Email"}</label>
              <Input
                type="email"
                value={draft.email}
                onChange={(event) => setDraft({ ...draft, email: event.value })}
              />
            </div>
            <div className="profile-meta">
              <strong>{t.lastLogin || "Last login"}</strong>
              <span>
                {currentUser?.lastLoginAt
                  ? new Intl.DateTimeFormat(locale?.localeCode || "en-US", {
                      dateStyle: "medium",
                      timeStyle: "short"
                    }).format(new Date(currentUser.lastLoginAt))
                  : t.firstSession || "First session"}
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
